/* eslint-disable no-undef */
import {
  databaseCreditPackPriceIds,
  productDatabaseCreditPackPriceIds,
} from 'pages/credit-plans/CreditCalculator';

export type LicenseType =
  | 'standard'
  | 'pro'
  | 'business'
  | 'enterprise'
  | 'none';

export const LICENSE_TIER_RANK: Record<LicenseType, number> = {
  none: 0,
  standard: 1,
  pro: 2,
  business: 3,
  enterprise: 4,
};

const ACTIVE_SUBSCRIPTION_STATUSES = ['active', 'trialing', 'past_due'];

const NON_LICENSE_METADATA_TYPES = new Set([
  'analysis_credit',
  'database_credit',
  'product_database_credit',
  'meter',
  'business_verification_addon',
]);

// Credit packs are billed as their own Stripe subscriptions/items and must
// never be counted as a seat of any license tier. Module names like
// "Product Database Credit Pack 50" contain "pro" ("PROduct") and would
// otherwise collide with the Pro tier's name match below.
const NON_LICENSE_PRICE_IDS = new Set(
  [
    ...Object.values(databaseCreditPackPriceIds),
    ...Object.values(productDatabaseCreditPackPriceIds),
    process.env.REACT_APP_ANALYZER_REFRESH_PRICE || '',
    process.env.REACT_APP_BUSINESS_VERIFICATION_CREDIT_PRICE || '',
    process.env.REACT_APP_BUSINESS_VERIFICATION_ADDON_PRICE || '',
  ].filter(Boolean),
);

const STANDARD_PRICE = process.env.REACT_APP_STANDARD_PRICE || '';
const PRO_PRICE = process.env.REACT_APP_PRO_PRICE || '';
const BUSINESS_PRICE = process.env.REACT_APP_BUSINESS_PRICE || '';
const ENTERPRISE_PRICE_IDS = new Set(
  [
    process.env.REACT_APP_ENTERPRISE_STARTER_PRICE,
    process.env.REACT_APP_ENTERPRISE_GROWTH_PRICE,
    process.env.REACT_APP_ENTERPRISE_SCALE_PRICE,
    process.env.REACT_APP_ENTERPRISE_PLUS_PRICE,
    process.env.REACT_APP_ENTERPRISE_MAX_PRICE,
  ].filter(Boolean),
);

const resolveModuleName = (item: any, subscription?: any): string =>
  String(
    item?.metadata?.moduleName ||
      item?.price?.metadata?.moduleName ||
      subscription?.metadata?.moduleName ||
      '',
  ).toLowerCase();

const resolvePriceId = (item: any): string => item?.price?.id || '';

export const isNonLicenseItem = (item: any, subscription?: any): boolean => {
  const type = String(
    item?.metadata?.type || subscription?.metadata?.type || '',
  ).toLowerCase();
  if (NON_LICENSE_METADATA_TYPES.has(type)) return true;

  const priceId = resolvePriceId(item);
  if (priceId && NON_LICENSE_PRICE_IDS.has(priceId)) return true;

  const moduleName = resolveModuleName(item, subscription);
  if (moduleName.includes('credit pack')) return true;
  // "Business Verification" would otherwise match the Business tier below.
  if (moduleName.includes('business verification')) return true;

  return false;
};

export const resolveLicenseTypeForItem = (
  item: any,
  subscription?: any,
): LicenseType | null => {
  if (isNonLicenseItem(item, subscription)) return null;

  const priceId = resolvePriceId(item);
  if (priceId) {
    if (priceId === STANDARD_PRICE) return 'standard';
    if (priceId === PRO_PRICE) return 'pro';
    if (priceId === BUSINESS_PRICE) return 'business';
    if (ENTERPRISE_PRICE_IDS.has(priceId)) return 'enterprise';
  }

  const moduleName = resolveModuleName(item, subscription);

  const moduleWords = new Set(moduleName.split(/[^a-z0-9]/).filter(Boolean));
  if (moduleWords.has('enterprise')) return 'enterprise';
  if (moduleWords.has('business')) return 'business';
  if (moduleWords.has('standard') || moduleWords.has('tariffs'))
    return 'standard';
  if (moduleWords.has('pro') || moduleWords.has('starter')) return 'pro';

  return null;
};

export const resolveItemQuantity = (item: any, subscription?: any): number => {
  const quantity = Number(item?.quantity ?? subscription?.quantity ?? 0);
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
};

/**
 * Every subscription belonging to a team, across all customers returned for an
 * email.
 *
 * A team can have more than one Stripe customer under the same name — checkout
 * creates one per attempt — and only one of them holds the live subscription.
 * Selecting the customer by name with `.find()` therefore takes whichever Stripe
 * listed first, which is the most recently created, and reports zero seats
 * whenever that one is the empty duplicate. Nothing in the response tells the
 * caller this happened: the lookup succeeded and a customer *was* found, so the
 * tab shows a team with a paid-up trial no licences at all, silently.
 *
 * `metadata.team_id` on the **subscription** is the reliable link, and the one
 * the server's own seat check uses. Deliberately not the customer's team_id: a
 * customer set up by hand can carry a different team's id while holding the
 * right team's subscription, so reading it from the customer reintroduces the
 * bug one level up.
 *
 * Subscriptions predating that metadata have none, so those fall back to the
 * customer-name match — this widens what is found, it never narrows it. The
 * fallback still skips a subscription explicitly tagged for a *different*
 * team: a same-named customer can hold another team's seats, and counting them
 * would show tiles the server's seat check (keyed on team_id) then refuses.
 */
export const selectTeamSubscriptions = (
  customers: any[],
  team: { id?: string; name?: string } | null | undefined,
): any[] => {
  const list = Array.isArray(customers) ? customers : [];
  const subscriptionsOf = (customer: any): any[] =>
    Array.isArray(customer?.subscriptions?.data)
      ? customer.subscriptions.data
      : [];

  const teamId = team?.id;
  const teamName = team?.name;
  const taggedForAnotherTeam = (subscription: any): boolean => {
    const tagged = subscription?.metadata?.team_id;
    return Boolean(tagged) && tagged !== teamId;
  };

  // One pass over both sets: a team that has bought since the metadata was
  // added still owns the seats on its older, untagged subscriptions.
  const selected: any[] = [];
  const seen = new Set<string>();
  for (const customer of list) {
    for (const subscription of subscriptionsOf(customer)) {
      const taggedForTeam =
        Boolean(teamId) && subscription?.metadata?.team_id === teamId;
      const sameNamedCustomer =
        Boolean(teamName) &&
        customer?.name === teamName &&
        !taggedForAnotherTeam(subscription);
      if (!taggedForTeam && !sameNamedCustomer) continue;
      const id = subscription?.id;
      if (id) {
        if (seen.has(id)) continue;
        seen.add(id);
      }
      selected.push(subscription);
    }
  }
  return selected;
};

export const buildLicenseInventory = (
  subscriptions: any[],
): Record<'standard' | 'pro' | 'business' | 'enterprise', number> => {
  const totals: Record<'standard' | 'pro' | 'business' | 'enterprise', number> =
    {
      standard: 0,
      pro: 0,
      business: 0,
      enterprise: 0,
    };

  const list = Array.isArray(subscriptions) ? subscriptions : [];
  list
    .filter((subscription) =>
      ACTIVE_SUBSCRIPTION_STATUSES.includes(subscription?.status),
    )
    .forEach((subscription) => {
      const items = Array.isArray(subscription?.items?.data)
        ? subscription.items.data
        : [];
      items.forEach((item: any) => {
        const licenseType = resolveLicenseTypeForItem(item, subscription);
        if (!licenseType || licenseType === 'none') return;
        const quantity = resolveItemQuantity(item, subscription);
        if (quantity <= 0) return;
        totals[licenseType] += quantity;
      });
    });

  return totals;
};
